/**
 * The button of a state that is not « à jour » (see sourceFix.ts). One button
 * per state, and it does the work:
 * - `sync`: runs the synchronisation right here when the user holds
 *   `sync_integrations` (pending state, then the screens reading the source
 *   are refreshed); otherwise it opens THAT connector
 *   (`/integrations?connecteur=<clé>`), where the reason is shown.
 * - `navigate`: opens the items or the setting concerned (identity in the URL).
 * - `external`: opens the record in the tool where it is corrected.
 */
import { useState, type MouseEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useHref } from 'react-router-dom';
import { withProviderLogo } from '../../components/c/ProviderLogo';
import { toast } from '../../components/ui/Toast';
import { usePermissions } from '../../hooks/usePermissions';
import { runIntegrationSync } from '../../lib/api';
import { safeString } from '../../lib/format';
import { useUiStore } from '../../store/ui';
import { refreshProjectionConsumers } from './integrationProjection';
import { useBusinessOsNavigation, type BusinessOsNavigationTarget } from './navigation';
import { CONNECTOR_LABEL, connectorTarget, isSourceConnector, type FixAction, type SourceConnector, type SourceFix } from './sourceFix';
import './fix-action.css';

function ExternalIcon() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 5h5v5M19 5l-8 8M17 14v5H5V7h5" />
    </svg>
  );
}

function buttonClass(primary: boolean): string {
  return `button button--compact fix-action${primary ? ' button--primary' : ''}`;
}

/**
 * A native link to the route (middle-click opens THE item in a new tab); a
 * plain click goes through the app navigation so the arrival screen also
 * receives its view state (DR-4: links for routes, buttons for actions).
 */
export function RouteLink({ target, className, children }: { target: BusinessOsNavigationTarget; className: string; children: ReactNode }) {
  const openTarget = useBusinessOsNavigation();
  const href = useHref(target.route);
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    openTarget(target);
  };
  return <a href={href} className={className} onClick={onClick}>{children}</a>;
}

/**
 * « Synchroniser Qonto »: executes the sync when allowed, else opens the
 * connector. The error of a failed run stays visible next to the button until
 * the next attempt (DR-1).
 */
export function SyncSourceButton({
  connector,
  label,
  primary = false,
  onSynced,
}: {
  connector: SourceConnector;
  label?: string;
  primary?: boolean;
  /** Called after a successful run, once the consumers were refreshed. */
  onSynced?: () => void;
}) {
  const { canSyncIntegrations, isLoading } = usePermissions();
  const selectedYear = useUiStore((state) => state.selectedYear);
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<'idle' | 'running' | 'error'>('idle');
  const [error, setError] = useState('');
  const name = CONNECTOR_LABEL[connector];

  // Until the right to synchronise is confirmed, the button opens the
  // connector (never a button that would then be refused by the server).
  if (isLoading || !canSyncIntegrations) {
    return <RouteLink target={connectorTarget(connector)} className={buttonClass(primary)}>{withProviderLogo(`Ouvrir le connecteur ${name}`)}</RouteLink>;
  }

  const run = async () => {
    if (phase === 'running') return;
    setPhase('running');
    setError('');
    try {
      const result = await runIntegrationSync(connector, { year: selectedYear, apply: false });
      if (!result.ok) throw new Error(safeString(result.error) || `La synchronisation ${name} a échoué.`);
      try {
        await refreshProjectionConsumers(queryClient, connector, selectedYear);
      } catch {
        // The sync itself succeeded: a failed refetch must not rewrite it as a failure.
      }
      setPhase('idle');
      const copied = Number(result.synced || 0) + Number(result.pulled_contacts || 0) + Number(result.pulled_deals || 0);
      toast.success(copied > 0 ? `${name} synchronisé : ${copied.toLocaleString('fr-FR')} élément(s) copiés` : `${name} synchronisé`);
      onSynced?.();
    } catch (failure) {
      setPhase('error');
      setError(failure instanceof Error && failure.message ? failure.message : `La synchronisation ${name} a échoué.`);
    }
  };

  return (
    <span className="fix-action__wrap">
      <button
        type="button"
        className={buttonClass(primary)}
        disabled={phase === 'running'}
        aria-busy={phase === 'running'}
        onClick={() => void run()}
      >
        {withProviderLogo(phase === 'running' ? `Synchronisation ${name}…` : label || `Synchroniser ${name}`)}
      </button>
      {phase === 'error' ? <span className="fix-action__error" role="alert">{error}</span> : null}
    </span>
  );
}

/** Renders the single action of a state (or nothing for an information). */
export function FixActionButton({ action, primary = false }: { action: FixAction | null | undefined; primary?: boolean }) {
  if (!action) return null;
  if (action.kind === 'sync') {
    return isSourceConnector(action.connector) ? <SyncSourceButton connector={action.connector} label={action.label} primary={primary} /> : null;
  }
  if (action.kind === 'external') {
    return (
      <a className={buttonClass(primary)} href={action.href} target="_blank" rel="noopener noreferrer">
        {withProviderLogo(action.label)}
        <ExternalIcon />
      </a>
    );
  }
  return <RouteLink target={action.target} className={buttonClass(primary)}>{withProviderLogo(action.label)}</RouteLink>;
}

/**
 * The real cause of a source pill that is not « à jour », and its single
 * action — for the ⓘ detail of a pill (SourceChip, SourceFreshness). Nothing
 * when the source is up to date.
 */
export function SourceFixDetail({ fix }: { fix: SourceFix | null | undefined }) {
  if (!fix || !fix.cause) return null;
  return (
    <div className="fix-state">
      <span className="fix-state__reason">{fix.reason}</span>
      <FixActionButton action={fix.action} />
    </div>
  );
}
